/**
 * N3 · Competência 6 (Estatística) · task-model H1.36.K01
 *
 *   "Localizar a média de um conjunto de dados como o ponto de equilíbrio."
 *
 * Caixas iguais sobre uma régua graduada de 0 a 10: cada caixa é um dado, posta sobre o
 * seu valor, e valores repetidos empilham. O aluno move o apoio, por baixo da régua,
 * até o ponto em que ela ficaria em equilíbrio — a média.
 *
 * A figura faz duas coisas que a lista de números não faz: mostra a FREQUÊNCIA (duas
 * caixas no mesmo lugar pesam o dobro) e dá à média um sentido físico. E ela não
 * entrega a resposta: a régua está presa por calços e não inclina enquanto o apoio se
 * move (ver `render/gangorra.ts`).
 *
 * A resposta é a posição do apoio — um seletor da figura —, por isso o item é N3. Os
 * erros com nome são os das outras medidas de centro: a mediana, a moda, o meio entre o
 * menor e o maior valor, a média das posições sem contar as caixas repetidas e o meio
 * da régua. Um erro que cai no mesmo ponto que a média, ou fora do alcance do seletor,
 * não entra na variante.
 */

import { formatBR } from '../../core/num.ts';
import { conta } from '../../core/pt.ts';
import type { Rng } from '../../core/rng.ts';
import { sampleWhere } from '../../core/sampling.ts';
import { gangorra } from '../../render/gangorra.ts';
import type { DifficultySourceCode } from '../../schema/difficulty.ts';
import type { ErrorCode } from '../../schema/errors.ts';
import {
  EST_CENTRO_DA_ESCALA,
  EST_MEDIA_POR_MEDIANA,
  EST_MODA_POR_MEDIA,
  EST_PONTO_MEDIO_EXTREMOS,
  EST_SEM_PONDERAR,
} from '../../schema/errors.ts';
import type { ItemTemplate, NumericEntryInteraction } from '../../schema/item.ts';
import { templateId } from '../../schema/item.ts';
import { br, strong, txt, type RichText } from '../../schema/richtext.ts';
import { skillCode, taskModelCode } from '../../schema/taxonomy.ts';

const MAX = 10;

interface Contexto {
  readonly texto: string;
  /** A média de quê, como o enunciado a nomeia. */
  readonly media: string;
  readonly lo: number;
  readonly hi: number;
}

const CONTEXTOS: readonly Contexto[] = [
  {
    texto:
      'Cada caixa sobre a régua representa a nota de um aluno numa prova de 0 a 10: a caixa fica sobre a nota, e notas iguais se empilham.',
    media: 'média das notas',
    lo: 2,
    hi: 10,
  },
  {
    // "Uma noite da semana" não servia: há variantes com 8 caixas.
    texto:
      'Cada caixa sobre a régua representa uma noite de Bia: a caixa fica sobre o número de horas que ela dormiu naquela noite, e noites com o mesmo número de horas se empilham.',
    media: 'média de horas de sono por noite',
    lo: 4,
    hi: 10,
  },
  {
    texto:
      'Cada caixa sobre a régua representa a nota, de 0 a 10, que um leitor deu a um livro: a caixa fica sobre a nota, e notas iguais se empilham.',
    media: 'média das notas',
    lo: 1,
    hi: 10,
  },
];

export interface Params {
  readonly contexto: number;
  /** Os dados, em ordem crescente: uma caixa por dado. */
  readonly valores: readonly number[];
}

function drawParams(rng: Rng): Params {
  const contexto = rng.int(0, CONTEXTOS.length - 1);
  const c = CONTEXTOS[contexto] as Contexto;
  const valores = sampleWhere(
    rng.fork('dados'),
    (g) => {
      const n = g.int(5, 8);
      return Array.from({ length: n }, () => g.int(c.lo, c.hi)).sort((a, b) => a - b);
    },
    (vs) => {
      const soma = vs.reduce((s, v) => s + v, 0);
      const pilhas = contagem(vs);
      return (
        soma % vs.length === 0 &&
        Math.max(...pilhas.values()) <= 3 &&
        pilhas.size >= 3 &&
        erros(vs).length >= 3
      );
    },
    { label: 'dados', maxTries: 400 },
  );
  return { contexto, valores };
}

function contagem(vs: readonly number[]): Map<number, number> {
  const m = new Map<number, number>();
  for (const v of vs) m.set(v, (m.get(v) ?? 0) + 1);
  return m;
}

const num = (v: number): string => formatBR(v, Number.isInteger(v) ? 0 : 1);
const somaTxt = (vs: readonly number[]): string => vs.map(num).join(' + ');

function mediana(vs: readonly number[]): number {
  const n = vs.length;
  return n % 2 === 1
    ? (vs[(n - 1) / 2] as number)
    : ((vs[n / 2 - 1] as number) + (vs[n / 2] as number)) / 2;
}

type Erro = NumericEntryInteraction['errorValues'][number];

/** Os erros alcançáveis nesta variante, com a conta de cada um. */
function erros(vs: readonly number[]): Erro[] {
  const n = vs.length;
  const soma = vs.reduce((s, v) => s + v, 0);
  const media = soma / n;
  const conta = `(${somaTxt(vs)}) ÷ ${n} = ${soma} ÷ ${n} = ${num(media)}`;
  const candidatos: { value: number; error: ErrorCode; explanation: RichText }[] = [];

  const med = mediana(vs);
  candidatos.push({
    value: med,
    error: EST_MEDIA_POR_MEDIANA,
    explanation: [
      txt(`${num(med)} é a mediana: o valor do meio quando os ${n} valores são postos em ordem. `),
      txt(`A média soma todos os valores e divide pela quantidade: ${conta}.`),
    ],
  });

  const pilhas = contagem(vs);
  const maisAlta = Math.max(...pilhas.values());
  const modas = [...pilhas.entries()].filter(([, k]) => k === maisAlta).map(([v]) => v);
  if (maisAlta >= 2 && modas.length === 1) {
    const moda = modas[0] as number;
    candidatos.push({
      value: moda,
      error: EST_MODA_POR_MEDIA,
      explanation: [
        txt(`${num(moda)} é a posição com mais caixas — a moda. `),
        txt(`A média leva em conta todas as ${n} caixas, e não só a pilha mais alta: ${conta}.`),
      ],
    });
  }

  const distintos = [...pilhas.keys()].sort((a, b) => a - b);
  const sd = distintos.reduce((s, v) => s + v, 0);
  candidatos.push({
    value: sd / distintos.length,
    error: EST_SEM_PONDERAR,
    explanation: [
      txt(
        `Você fez a média das posições ocupadas, sem contar quantas caixas há em cada uma: ` +
          `(${somaTxt(distintos)}) ÷ ${distintos.length} = ${num(sd / distintos.length)}. `,
      ),
      txt(`Uma posição com duas caixas conta duas vezes: ${conta}.`),
    ],
  });

  const lo = vs[0] as number;
  const hi = vs[n - 1] as number;
  candidatos.push({
    value: (lo + hi) / 2,
    error: EST_PONTO_MEDIO_EXTREMOS,
    explanation: [
      txt(
        `${num((lo + hi) / 2)} é o meio do caminho entre o menor e o maior valor, ${num(lo)} e ${num(hi)}. `,
      ),
      txt(`Mas o equilíbrio depende de todas as caixas, e não só das duas das pontas: ${conta}.`),
    ],
  });

  candidatos.push({
    value: MAX / 2,
    error: EST_CENTRO_DA_ESCALA,
    explanation: [
      txt(
        `${num(MAX / 2)} é o meio da régua. Mas o apoio equilibra as caixas, e não a régua vazia: `,
      ),
      txt(`com as caixas onde estão, o ponto de equilíbrio é a média, ${conta}.`),
    ],
  });

  // Só o que o seletor alcança (de meio em meio), longe da média e sem repetir ponto.
  const out: Erro[] = [];
  for (const e of candidatos) {
    const noPasso = Math.abs(e.value * 2 - Math.round(e.value * 2)) < 1e-9;
    if (!noPasso || e.value < 0 || e.value > MAX) continue;
    if (Math.abs(e.value - media) < 0.5 - 1e-9) continue;
    if (out.some((o) => Math.abs(o.value - e.value) < 1e-9)) continue;
    out.push(e);
  }
  return out;
}

function descreverCaixas(vs: readonly number[]): string {
  return [...contagem(vs).entries()]
    .map(([v, k]) => `${conta(k, 'caixa')} sobre o ${num(v)}`)
    .join(', ');
}

export const gangorraMedia: ItemTemplate<Params, number> = {
  id: templateId('h1-36-K01-gangorra'),
  version: 1,
  taskModel: taskModelCode('H1.36.K01'),
  skills: [skillCode('H1.36')],
  axis: 'II',
  level: 'N3',
  title: 'A média como ponto de equilíbrio',
  responseModel: '2PL',
  difficultySources: ['steps.2'],
  params: drawParams,
  solve: (p) => p.valores.reduce((s, v) => s + v, 0) / p.valores.length,
  buggy: [],
  variantDifficulty(p) {
    const fontes: DifficultySourceCode[] = [];
    if (p.valores.length >= 7) fontes.push('steps.3plus');
    if (Math.max(...contagem(p.valores).values()) >= 3) fontes.push('select-information');
    return fontes;
  },
  build(p, media) {
    const c = CONTEXTOS[p.contexto] as Contexto;
    const n = p.valores.length;
    const soma = p.valores.reduce((s, v) => s + v, 0);
    const caixas = descreverCaixas(p.valores);
    const alt =
      `Régua de 0 a ${MAX}, com uma marca em cada número inteiro e os números só nas pontas, presa por dois calços. ` +
      `Caixas iguais sobre ela: ${caixas}. Um apoio triangular, por baixo, é movido pelo seletor.`;
    const esquerda = p.valores.filter((v) => v < media).map((v) => media - v);
    const direita = p.valores.filter((v) => v > media).map((v) => v - media);
    const interaction: NumericEntryInteraction = {
      kind: 'numeric-entry',
      decimals: 1,
      keyValue: media,
      // O seletor anda de meio em meio: com folga menor que 0,25, dois diagnósticos
      // vizinhos nunca se confundem.
      absTol: 0.2,
      errorValues: erros(p.valores),
      viaControl: { stimulusId: 'gangorra', controlId: 'apoio' },
    };
    return {
      stem: [
        txt(c.texto),
        br,
        txt(
          'Uma régua leve, com caixas iguais apoiadas nela, fica em equilíbrio quando o apoio está exatamente na ',
        ),
        strong(c.media),
        txt('. Por enquanto, dois calços prendem a régua nas pontas.'),
      ],
      comando: [
        txt(
          'Mova o apoio até a posição em que a régua fica em equilíbrio quando os calços forem retirados.',
        ),
      ],
      stimuli: [
        {
          kind: 'manipulable',
          motion: 'interactive',
          audio: { need: 'none' },
          id: 'gangorra',
          alt,
          controls: [
            {
              id: 'apoio',
              label: [txt('Posição do apoio')],
              min: 0,
              max: MAX,
              step: 0.5,
              initial: 0,
              format: num,
            },
          ],
          frame: (v) => {
            const apoio = v['apoio'] ?? 0;
            return gangorra({
              posicoes: p.valores,
              apoio,
              max: MAX,
              title: 'Régua com caixas e um apoio',
              desc: `${alt} O apoio está na posição ${num(apoio)}.`,
            });
          },
        },
      ],
      interaction,
      dicas: [
        [
          txt(
            'O apoio equilibra a régua na média: some as posições de todas as caixas e divida pela quantidade de caixas.',
          ),
        ],
        [txt(`Caixas empilhadas contam uma vez cada: são ${n} caixas ao todo.`)],
        [
          txt(
            'Para conferir: somando as distâncias até o apoio, as caixas da esquerda e as da direita dão o mesmo total.',
          ),
        ],
      ],
      rationale: [
        strong('Resolução. '),
        txt(`São ${n} caixas: ${caixas}. A média é a soma dos valores dividida pela quantidade:`),
        br,
        txt(`(${somaTxt(p.valores)}) ÷ ${n} = ${soma} ÷ ${n} = ${num(media)}.`),
        br,
        txt(
          `Conferindo o equilíbrio: somando as distâncias até ${num(media)}, as caixas da esquerda dão ` +
            `${esquerda.length > 0 ? `${somaTxt(esquerda)} = ${num(esquerda.reduce((s, x) => s + x, 0))}` : '0'}, ` +
            `e as da direita, ${direita.length > 0 ? `${somaTxt(direita)} = ${num(direita.reduce((s, x) => s + x, 0))}` : '0'} — o mesmo valor.`,
        ),
      ],
      declared: n >= 7 ? 'dificil' : 'media',
    };
  },
  validate(built, p) {
    if (built.interaction.kind !== 'numeric-entry') return 'interação inesperada';
    const media = p.valores.reduce((s, v) => s + v, 0) / p.valores.length;
    if (media <= 0 || media >= MAX) return 'média fora do alcance do apoio';
    return null;
  },
};
